import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useMemo,
} from "react";

export type Position = {
  x: number;
  y: number;
  id: string;
  originalX: number;
  originalY: number;
};

interface Result {
  position: string;
  reactionTime: number | string;
}

interface BilanProps {
  caseSize: number;
  isPausedAndNotFullScreenRef: React.MutableRefObject<boolean>;
  predeterminedCells: string[];
  saveResults: (results: Result[]) => void;
  nextModule: () => void;
  intermediateSave: (
    results: Result[],
    targetEnCours: Position[],
    initialTargetsLength: number
  ) => void;
  status: "EN_COURS" | "PREVUE" | "REALISEE";
  targetEnCours_Zf_Bilan: Position[] | null;
  initialTargetsLengthEncours_Zf_Bilan: number | null;
  resultsZF: Result[] | null;
}

const ZoneFrontiere = forwardRef(
  (
    {
      caseSize,
      isPausedAndNotFullScreenRef,
      predeterminedCells,
      saveResults,
      nextModule,
      intermediateSave,
      status,
      targetEnCours_Zf_Bilan,
      initialTargetsLengthEncours_Zf_Bilan,
      resultsZF,
    }: BilanProps,
    ref
  ) => {
    const [currentTarget, setCurrentTarget] = useState<Position | null>(null);
    const [targetColor, setTargetColor] = useState("white");
    const [startTime, setStartTime] = useState<number | null>(null);
    const [results, setResults] = useState<Result[]>(resultsZF || []);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [ciblesRestantes, setCiblesRestantes] = useState(
      predeterminedCells.length
    );
    const [canShowTarget, setCanShowTarget] = useState(true);
    const cellWidth = (5.5 / 4) * caseSize;
    const cellHeight = caseSize;
    const targetPositionsRef = useRef<Position[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const timerRef2 = useRef<NodeJS.Timeout | null>(null);
    const reducedTargetsCountRef = useRef(0);
    const hasReactedRef = useRef(false);
    const [quadrillageVisible, setQuadrillageVisible] = useState(false);

    const showNewTargetRef = useRef(() => {});

    useEffect(() => {
      setCiblesRestantes(predeterminedCells.length);
    }, [predeterminedCells]);

    const memoizedPredeterminedCells = useMemo(
      () => predeterminedCells,
      [predeterminedCells]
    );
    const parseCellIdentifier = useCallback(
      (cell: string): { x: number; y: number } => {
        const column = cell.charCodeAt(0) - 65;
        const row = parseInt(cell.slice(1), 10) - 1;
        return { x: column * cellWidth, y: row * cellHeight };
      },
      [cellHeight, cellWidth]
    );

    const calculateCanvasDimensions = useCallback(() => {
      const maxCanvasWidth = window.innerWidth;
      const maxCanvasHeight = window.innerHeight;

      let maxColumns = 16;
      let maxRows = 12;

      let canvasWidth = maxColumns * cellWidth;
      let canvasHeight = maxRows * cellHeight;

      let columnsToRemove = 0;
      if (canvasWidth > maxCanvasWidth) {
        const excessColumns = Math.ceil(
          (canvasWidth - maxCanvasWidth) / cellWidth
        );
        columnsToRemove = Math.ceil(excessColumns / 2);
        maxColumns -= 2 * columnsToRemove;
        canvasWidth = maxColumns * cellWidth;
      }

      let rowsToRemove = 0;
      if (canvasHeight > maxCanvasHeight) {
        const excessRows = Math.ceil(
          (canvasHeight - maxCanvasHeight) / cellHeight
        );
        rowsToRemove = Math.ceil(excessRows / 2);
        maxRows -= 2 * rowsToRemove;
        canvasHeight = maxRows * cellHeight;
      }

      return {
        canvasWidth,
        canvasHeight,
        maxColumns,
        maxRows,
        columnsToRemove,
        rowsToRemove,
      };
    }, [cellWidth, cellHeight]);

    useEffect(() => {
      reducedTargetsCountRef.current = 0;

      const { maxColumns, maxRows, columnsToRemove, rowsToRemove } =
        calculateCanvasDimensions();

      const generatePositions = () => {
        const basePositions: Position[] = memoizedPredeterminedCells
          .map((cell, index) => {
            const { x, y } = parseCellIdentifier(cell);

            // Calculer la position originale dans un canvas sans réduction
            const originalColumn = x / cellWidth;
            const originalRow = y / cellHeight;

            // Calculer la position réduite après suppression symétrique des colonnes/lignes
            const column = originalColumn - columnsToRemove; // Ajustement à gauche/droite
            const row = originalRow - rowsToRemove; // Ajustement en haut/bas

            // Vérifier si la position est dans les colonnes/lignes supprimées
            const columnOutOfBounds = column < 0 || column >= maxColumns;
            const rowOutOfBounds = row < 0 || row >= maxRows;

            // Si la cible est en dehors des limites, ne pas la dessiner
            if (columnOutOfBounds || rowOutOfBounds) {
              reducedTargetsCountRef.current += 1; // Incrémenter le nombre de cibles réduites
              return null;
            }

            // Retourner la position réduite et la position originale
            return {
              x: column * cellWidth,
              y: row * cellHeight,
              originalX: originalColumn * cellWidth,
              originalY: originalRow * cellHeight,
              id: `predetermined-${index}`,
            };
          })
          .filter((position): position is Position => position !== null); // Filtrer les positions nulles

        const allPositions = [...basePositions].sort(() => Math.random() - 0.5);
        if (
          status === "EN_COURS" &&
          targetEnCours_Zf_Bilan &&
          initialTargetsLengthEncours_Zf_Bilan !== null
        ) {
          targetPositionsRef.current = targetEnCours_Zf_Bilan;
        } else {
          targetPositionsRef.current = allPositions;
        }
      };

      generatePositions();
    }, [
      calculateCanvasDimensions,
      parseCellIdentifier,
      memoizedPredeterminedCells,
      cellWidth,
      cellHeight,
    ]);

    const convertToGridNotation = useCallback(
      (x: number, y: number) => {
        const column = String.fromCharCode(65 + x / cellWidth);
        const row = y / cellHeight + 1;
        return `${column}${row}`;
      },
      [cellHeight, cellWidth]
    );

    useEffect(() => {
      const handleKeyPress = (e: { key: string }) => {
        if (e.key === "s") {
          showNewTargetRef.current();
        }
        if (e.key === "q" || e.key === "Q") {
          setQuadrillageVisible((prev) => !prev);
        }
        if (e.key === "ArrowRight") {
          saveResults(results);
          nextModule();
        }
      };
      window.addEventListener("keydown", handleKeyPress);
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }, [results, isPausedAndNotFullScreenRef]);

    useEffect(() => {
      const handleKeyPress = (e: { key: string }) => {
        if (
          e.key === " " &&
          currentTarget &&
          !isPausedAndNotFullScreenRef.current &&
          startTime !== null &&
          !hasReactedRef.current
        ) {
          hasReactedRef.current = true;
          const reactionTime = new Date().getTime() - startTime;
          const originalGridPosition = convertToGridNotation(
            currentTarget.originalX,
            currentTarget.originalY
          ); // Case d'origine
          if (reactionTime <= 800) {
            setResults((prevResults) => [
              ...prevResults,
              { position: originalGridPosition, reactionTime },
            ]);
            if (timerRef2.current !== null) {
              clearTimeout(timerRef2.current);
            }
          }
        }
      };

      window.addEventListener("keydown", handleKeyPress);
      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }, [
      currentTarget,
      startTime,
      isPausedAndNotFullScreenRef,
      convertToGridNotation,
    ]);

    const showNewTarget = useCallback(() => {
      if (
        targetPositionsRef.current.length > 0 &&
        ciblesRestantes > 0 &&
        canShowTarget &&
        !isPausedAndNotFullScreenRef.current
      ) {
        setCanShowTarget(false);
        const delai = Math.random() * 2000 + 1000;

        timerRef.current = setTimeout(() => {
          const newTarget = targetPositionsRef.current.pop();
          if (newTarget) {
            const originalGridPosition = convertToGridNotation(
              newTarget.originalX,
              newTarget.originalY
            ); // Case d'origine
            // console.log("originalGridPosition", originalGridPosition);
            setCurrentTarget(newTarget);
            hasReactedRef.current = false;
            setTargetColor("white");
            setStartTime(new Date().getTime());

            setTimeout(() => {
              setTargetColor("black");
            }, 150);

            timerRef2.current = setTimeout(() => {
              setResults((prevResults) => [
                ...prevResults,
                { position: originalGridPosition, reactionTime: "Trop long" },
              ]);
            }, 800);

            setTimeout(() => {
              setCurrentTarget(null);
              setCanShowTarget(true);
              setCiblesRestantes((prev) => prev - 1);
              if (
                targetPositionsRef.current.length > 0 &&
                ciblesRestantes > 0
              ) {
                showNewTarget();
              }
            }, 800);
          }
        }, delai);
      }
      // else {
      //   console.info("showNewTarget not executed");
      // }
    }, [
      canShowTarget,
      ciblesRestantes,
      isPausedAndNotFullScreenRef,
      convertToGridNotation,
    ]);

    useImperativeHandle(ref, () => ({
      start: () => {
        showNewTargetRef.current();
      },
    }));

    useEffect(() => {
      showNewTargetRef.current = showNewTarget;
    }, [showNewTarget]);

    useEffect(() => {
      return () => {
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
        }
      };
    }, []);
    useEffect(() => {
      const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

      const canvas = canvasRef.current;
      if (canvas) {
        const context = canvas.getContext("2d");
        if (context) {
          context.clearRect(0, 0, canvas.width, canvas.height); // Effacer l'ancien contenu
          context.fillStyle = "black";
          context.fillRect(0, 0, canvasWidth, canvasHeight);

          if (quadrillageVisible) {
            // Couleur des lignes du quadrillage
            context.strokeStyle = "white";
            context.lineWidth = 1;

            // Dessiner les lignes verticales
            for (let x = 0; x <= canvasWidth; x += cellWidth) {
              context.beginPath();
              context.moveTo(x, 0); // Commencer en haut
              context.lineTo(x, canvasHeight); // Descendre jusqu'en bas
              context.stroke();
            }

            // Dessiner les lignes horizontales
            for (let y = 0; y <= canvasHeight; y += cellHeight) {
              context.beginPath();
              context.moveTo(0, y); // Commencer à gauche
              context.lineTo(canvasWidth, y); // Aller jusqu'à droite
              context.stroke();
            }
          }

          const centerX = canvasWidth / 2;
          const centerY = canvasHeight / 2;
          const crossLength = (caseSize / 4) * 0.7;

          context.beginPath();
          context.strokeStyle = "white";
          context.moveTo(centerX - crossLength / 2, centerY);
          context.lineTo(centerX + crossLength / 2, centerY);
          context.stroke();

          context.beginPath();
          context.moveTo(centerX, centerY - crossLength / 2);
          context.lineTo(centerX, centerY + crossLength / 2);
          context.stroke();

          // Redessiner les cibles uniquement dans les nouvelles limites du canvas
          if (currentTarget) {
            const radius = caseSize / (4 * 2); // 1 cm diameter

            // Pour centrer la cible, ajoutez `caseSize / 2` à `x` et `y`
            const centeredX = currentTarget.x + cellWidth / 2;
            const centeredY = currentTarget.y + cellHeight / 2;

            context.fillStyle = targetColor;
            context.beginPath();
            context.arc(centeredX, centeredY, radius, 0, Math.PI * 2);
            context.fill();
          }
        }
      }
    }, [
      currentTarget,
      targetColor,
      calculateCanvasDimensions,
      cellHeight,
      cellWidth,
    ]);

    useEffect(() => {
      let initialTargetsLength;
      if (
        status === "EN_COURS" &&
        initialTargetsLengthEncours_Zf_Bilan !== null
      ) {
        initialTargetsLength = initialTargetsLengthEncours_Zf_Bilan;
      } else {
        initialTargetsLength =
          predeterminedCells.length - reducedTargetsCountRef.current;
      }
      console.log(
        "initialTargetsLength", initialTargetsLength, predeterminedCells.length, reducedTargetsCountRef.current)
      const shouldSaveFinalResult =
        results.length === initialTargetsLength &&
        predeterminedCells.length > 0;
      const shouldSaveIntermediateResult =
        results.length % 5 === 0 &&
        results.length > 0 &&
        !shouldSaveFinalResult;

      if (shouldSaveIntermediateResult) {
        intermediateSave(
          results,
          targetPositionsRef.current,
          initialTargetsLength
        );
      }

      if (shouldSaveFinalResult) {
        saveResults(results);
        nextModule();
      }
    }, [results, predeterminedCells.length]);

    const { canvasWidth, canvasHeight } = calculateCanvasDimensions();

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "black",
        }}
      >
        <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} />
      </div>
    );
  }
);

export default ZoneFrontiere;
